import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ToastProvider } from '../components/ui/Toast';
import { CandidateTable } from '../components/CandidateTable';
import { CandidateModal } from '../components/CandidateModal';
import { SchedulePhoneScreenModal } from '../components/SchedulePhoneScreenModal';
import { ScheduleInterviewModal } from '../components/ScheduleInterviewModal';
import { SetFollowUpModal } from '../components/SetFollowUpModal';
import { AddCandidateModal } from '../components/AddCandidateModal';
import { HireCandidateModal } from '../components/HireCandidateModal';
import { CompanyDirectoryModal } from '../components/CompanyDirectoryModal';
import { OfferLetterModal } from '../components/OfferLetterModal';
import { Candidate } from '../types/hr.types';
import { mockCandidates } from '../utils/mockData';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/Logo';
import { Phone, Users, UserCheck, FileText, Clock, Plus, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type RecruitingTab = 'phone-screens' | 'in-person' | 'offers' | 'accepted' | 'follow-up';
export function Recruiting() {
  const [activeTab, setActiveTab] = useState<RecruitingTab>('phone-screens');
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [schedulingCandidate, setSchedulingCandidate] = useState<Candidate | null>(null);
  const [followUpCandidate, setFollowUpCandidate] = useState<Candidate | null>(null);
  const [hireCandidate, setHireCandidate] = useState<Candidate | null>(null);
  const [directoryCandidate, setDirectoryCandidate] = useState<{
    candidate: Candidate;
    next: Candidate['status'];
  } | null>(null);
  const [offerCandidate, setOfferCandidate] = useState<Candidate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPhoneScreenModalOpen, setIsPhoneScreenModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [isHireModalOpen, setIsHireModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDirectoryModalOpen, setIsDirectoryModalOpen] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addMenuPos, setAddMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const addMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [addDefaultStatus, setAddDefaultStatus] = useState<Candidate['status']>('Phone Screen');
  // Position the stage menu relative to the trigger using fixed coords so it
  // escapes the header's overflow-hidden clipping.
  useEffect(() => {
    if (!addMenuOpen) return;
    const updatePos = () => {
      const rect = addMenuTriggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setAddMenuPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right)
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [addMenuOpen]);
  const [candidates, setCandidates] = useState<Candidate[]>(mockCandidates);
  const filteredCandidates = useMemo(() => {
    if (activeTab === 'phone-screens') return candidates.filter(c => c.status === 'Phone Screen');
    if (activeTab === 'in-person') return candidates.filter(c => c.status === 'In Person Interview');
    if (activeTab === 'offers') return candidates.filter(c => c.status === 'Offer Sent' || c.status === 'Waiting on Response');
    if (activeTab === 'follow-up') return candidates.filter(c => c.status === 'Follow Up Later' || c.status === 'Hired Elsewhere' || c.status === 'Offer Declined');
    return candidates.filter(c => c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start');
  }, [candidates, activeTab]);
  const phoneScreenCount = candidates.filter(c => c.status === 'Phone Screen').length;
  const inPersonCount = candidates.filter(c => c.status === 'In Person Interview').length;
  const offersCount = candidates.filter(c => c.status === 'Offer Sent' || c.status === 'Waiting on Response').length;
  const followUpCount = candidates.filter(c => c.status === 'Follow Up Later' || c.status === 'Hired Elsewhere' || c.status === 'Offer Declined').length;
  const acceptedCount = candidates.filter(c => c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start').length;
  const handleCandidateClick = (candidate: Candidate) => {
    setSelectedCandidate(candidate);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCandidate(null), 300);
  };
  const handleCreateCandidate = (newCandidate: Candidate) => {
    setCandidates(prev => [...prev, newCandidate]);
  };
  const handleUpdateCandidate = (updatedCandidate: Candidate) => {
    setCandidates(prev => prev.map(c => c.id === updatedCandidate.id ? updatedCandidate : c));
    setSelectedCandidate(updatedCandidate);
  };
  const handleOpenSchedule = (candidate: Candidate) => {
    setSchedulingCandidate(candidate);
    setIsScheduleModalOpen(true);
  };
  const handleScheduleConfirm = (date: Date) => {
    if (schedulingCandidate) {
      const updatedCandidate = {
        ...schedulingCandidate,
        status: 'In Person Interview' as const,
        inPersonInterviewDate: date.toISOString()
      };
      handleUpdateCandidate(updatedCandidate);
      setIsScheduleModalOpen(false);
      setSchedulingCandidate(null);
    }
  };
  const handleOpenFollowUp = (candidate: Candidate) => {
    setFollowUpCandidate(candidate);
    setIsFollowUpModalOpen(true);
  };
  const handleHire = (candidate: Candidate) => {
    // Open the Hire modal so the user can pick a start date.
    setHireCandidate(candidate);
    setIsHireModalOpen(true);
  };
  const handleHireConfirm = (candidate: Candidate, {
    startDate,
    offerDecisionDate
  }: {
    startDate: string;
    offerDecisionDate: string;
  }) => {
    handleUpdateCandidate({
      ...candidate,
      status: 'Offer Accepted',
      offerDecisionDate,
      desiredStartDate: startDate,
      followUpDate: undefined,
      followUpReason: undefined
    });
    setIsHireModalOpen(false);
    setTimeout(() => setHireCandidate(null), 300);
  };
  const handleOpenOffer = (candidate: Candidate) => {
    setOfferCandidate(candidate);
    setIsOfferModalOpen(true);
  };
  const handleSendOfferConfirm = (candidate: Candidate, {
    offerAmount
  }: {
    offerAmount: number;
  }) => {
    const today = new Date().toISOString().split('T')[0];
    handleUpdateCandidate({
      ...candidate,
      status: 'Offer Sent',
      offerSentDate: today,
      offerAmount
    });
    setIsOfferModalOpen(false);
    setTimeout(() => setOfferCandidate(null), 300);
  };
  const handleAdvanceOnboarding = (candidate: Candidate, next: Candidate['status']) => {
    setDirectoryCandidate({
      candidate,
      next
    });
    setIsDirectoryModalOpen(true);
  };
  const handleDirectorySave = (candidate: Candidate, next: Candidate['status']) => {
    handleUpdateCandidate({
      ...candidate,
      status: next
    });
  };
  const handleOfferStatusChange = (candidate: Candidate, newStatus: Candidate['status']) => {
    // Follow Up Later requires a date/reason — open the dedicated modal.
    if (newStatus === 'Follow Up Later') {
      handleOpenFollowUp({
        ...candidate,
        status: 'Follow Up Later'
      });
      return;
    }
    // Hiring requires a start date — open the dedicated modal.
    if (newStatus === 'Offer Accepted') {
      setHireCandidate(candidate);
      setIsHireModalOpen(true);
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const patch: Partial<Candidate> = {
      status: newStatus
    };
    if (newStatus === 'Offer Declined') {
      patch.offerDecisionDate = today;
    }
    handleUpdateCandidate({
      ...candidate,
      ...patch
    });
  };
  const handleFollowUpSave = (updated: Candidate) => {
    handleUpdateCandidate(updated);
  };
  const openAddWithStatus = (status: Candidate['status']) => {
    setAddDefaultStatus(status);
    setIsAddModalOpen(true);
    setAddMenuOpen(false);
  };
  const tabs: {
    id: RecruitingTab;
    label: string;
    icon: any;
    count: number;
  }[] = [{
    id: 'phone-screens',
    label: 'Phone Screens',
    icon: Phone,
    count: phoneScreenCount
  }, {
    id: 'in-person',
    label: 'Interviews',
    icon: Users,
    count: inPersonCount
  }, {
    id: 'offers',
    label: 'Offers',
    icon: FileText,
    count: offersCount
  }, {
    id: 'accepted',
    label: 'Hired',
    icon: UserCheck,
    count: acceptedCount
  }, {
    id: 'follow-up',
    label: 'Follow Up',
    icon: Clock,
    count: followUpCount
  }];
  const activeTabLabel = {
    'phone-screens': 'Phone Screen Candidates',
    'in-person': 'In-Person Interviews',
    offers: 'Active Offers',
    'follow-up': 'Follow Ups',
    accepted: 'Accepted Candidates'
  }[activeTab];
  const nextDateLabel = {
    'phone-screens': 'Phone Screen Date',
    'in-person': 'Interview Date',
    offers: 'Offer Sent Date',
    'follow-up': 'Follow-up Date',
    accepted: 'Start Date'
  }[activeTab];
  return <ToastProvider data-id="element-1944">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50/30 to-gray-50 p-6 md:p-8 font-sans text-gray-900" data-id="element-1945">
        <div className="max-w-[1600px] mx-auto space-y-6" data-id="element-1946">
          <header className="relative overflow-hidden rounded-2xl bg-[#1B2B6B] p-8 md:p-10 shadow-xl shadow-[#1B2B6B]/30" data-id="element-1947">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15)_0%,_transparent_60%)]" data-id="element-1948"></div>
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-white/10 to-transparent" data-id="element-1949"></div>
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8" data-id="element-1950">
              <div className="flex items-center gap-8" data-id="element-1951">
                <div className="flex-shrink-0 bg-white px-3 py-2 rounded-xl shadow-sm" data-id="element-1952">
                  <Logo className="h-10 md:h-12 w-auto" data-id="element-1953" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1954">
                  <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight drop-shadow-sm" data-id="element-1955">
                    Recruiting Pipeline
                  </h1>
                  <p className="text-orange-100 mt-1 text-sm md:text-base" data-id="element-1956">
                    Manage candidates, track interviews, and oversee the hiring
                    process.
                  </p>
                </div>
              </div>
              {/* Add Candidate CTA — all candidates start at Phone Screen */}
              <div className="relative z-10 self-end md:self-center" data-id="element-1957">
                <Button variant="secondary" onClick={() => {
                setAddDefaultStatus('In Person Interview');
                setIsAddModalOpen(true);
              }} leftIcon={<Plus className="h-4 w-4" data-id="element-1959" />} className="bg-white hover:bg-white border-none shadow-lg" data-id="element-1958">
                  Add Candidate
                </Button>
              </div>
            </div>
          </header>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1.5 overflow-x-auto" data-id="element-1960">
            <div className="relative flex min-w-max" data-id="element-1961">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-semibold rounded-lg transition-colors z-10 whitespace-nowrap ${isActive ? 'text-orange-700' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1962">
                    {isActive && <motion.div layoutId="activeRecruitingTab" className="absolute inset-0 bg-orange-50 rounded-lg border border-orange-100" transition={{
                  type: 'spring',
                  bounce: 0.2,
                  duration: 0.5
                }} data-id="element-1963" />}
                    <span className="relative z-10 flex items-center gap-2" data-id="element-1964">
                      <Icon className="h-4 w-4" data-id="element-1965" />
                      {tab.label}
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${isActive ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1966">
                        {tab.count}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Table */}
          <div className="space-y-4" data-id="element-1967">
            <div className="flex items-center justify-between" data-id="element-1968">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-1969">
                <div className="h-1 w-8 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full" data-id="element-1970"></div>
                {activeTabLabel}
              </h2>
              <p className="text-xs text-gray-500" data-id="element-1971">
                {filteredCandidates.length} candidate
                {filteredCandidates.length === 1 ? '' : 's'}
              </p>
            </div>
            <CandidateTable candidates={filteredCandidates} onCandidateClick={handleCandidateClick} onScheduleInterview={handleOpenSchedule} onHire={handleHire} onSendOffer={handleOpenOffer} onSetFollowUp={handleOpenFollowUp} onStatusChange={handleOfferStatusChange} onAdvanceOnboarding={handleAdvanceOnboarding} nextDateLabel={nextDateLabel} emptyMessage={`No candidates in ${activeTabLabel.toLowerCase()}.`} data-id="element-1972" />
          </div>
        </div>

        <CandidateModal candidate={selectedCandidate} isOpen={isModalOpen} onClose={handleCloseModal} onUpdate={handleUpdateCandidate} onOpenSchedule={handleOpenSchedule} onOpenFollowUp={handleOpenFollowUp} onOpenHire={handleHire} onOpenOffer={handleOpenOffer} data-id="element-1973" />
        <SchedulePhoneScreenModal isOpen={isPhoneScreenModalOpen} onClose={() => setIsPhoneScreenModalOpen(false)} onCreate={handleCreateCandidate} data-id="element-1974" />
        <ScheduleInterviewModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} candidate={schedulingCandidate} onSchedule={handleScheduleConfirm} data-id="element-1975" />
        <SetFollowUpModal isOpen={isFollowUpModalOpen} onClose={() => {
        setIsFollowUpModalOpen(false);
        setTimeout(() => setFollowUpCandidate(null), 200);
      }} candidate={followUpCandidate} onSave={handleFollowUpSave} data-id="element-1976" />
        <AddCandidateModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onCreate={handleCreateCandidate} defaultStatus={addDefaultStatus} data-id="element-1977" />
        <HireCandidateModal isOpen={isHireModalOpen} onClose={() => {
        setIsHireModalOpen(false);
        setTimeout(() => setHireCandidate(null), 200);
      }} candidate={hireCandidate} onConfirm={handleHireConfirm} data-id="element-1978" />
        <CompanyDirectoryModal isOpen={isDirectoryModalOpen} onClose={() => {
        setIsDirectoryModalOpen(false);
        setTimeout(() => setDirectoryCandidate(null), 200);
      }} candidate={directoryCandidate?.candidate || null} nextStatus={directoryCandidate?.next} onSave={handleDirectorySave} data-id="element-1979" />
        <OfferLetterModal isOpen={isOfferModalOpen} onClose={() => {
        setIsOfferModalOpen(false);
        setTimeout(() => setOfferCandidate(null), 200);
      }} candidate={offerCandidate} onSend={handleSendOfferConfirm} data-id="element-1980" />
      </div>
    </ToastProvider>;
}